import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";

const MAX_PAGES = 10;
const DEFAULT_PAGE_SIZE = 20;

interface Page {
    number: number;
    text: string;
    active: boolean;
    disabled: boolean;
}

@Component({
    selector: "pagination-detail",
    template: `
        <div *ngIf="totalCount > pageSize" test-id="page-detail">
            <ul class="pagination-sm pagination">
                <li *ngFor="let page of pages"
                    [ngClass]="{current: page.active, unavailable: page.disabled}"
                    [attr.test-class]="'page-button-' + page.text">
                    <button (click)="selectPage(page)" [innerHtml]="page.text" [disabled]="page.disabled"
                            test-class="select-page"></button>
                </li>
            </ul>
        </div>
    `,
    styles: ["button[disabled] { opacity: 1; }"],
})
export class PaginationDetailComponent implements OnChanges {

    @Input() totalCount: number;
    @Input() pageNumber: number;
    @Input() pageSize: number;
    @Input() hideBoundaryLinks: boolean;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onSelectPage = new EventEmitter<number>();

    pages: Page[];

    ngOnChanges() {
        this.pageSize = this.pageSize || DEFAULT_PAGE_SIZE;

        const totalPages = this.pageSize < 1 ? 1 : Math.ceil(this.totalCount / this.pageSize);
        this.pages = this.getPages(this.pageNumber, Math.max(totalPages || 0, 1));
    }

    selectPage(page: Page) {
        this.onSelectPage.emit(page.number);
    }

    private getPages(currentPage, totalPages) {
        const pages: Page[] = [];

        let startPage = 1, endPage = totalPages;

        // recompute if it needs more pages than we can display
        if (MAX_PAGES < totalPages) {
            // Current page is displayed in the middle of the visible ones
            startPage = Math.max(currentPage - Math.floor(MAX_PAGES / 2), 1);
            endPage   = startPage + MAX_PAGES - 1;

            // Adjust if limit is exceeded
            if (endPage > totalPages) {
                endPage   = totalPages;
                startPage = endPage - MAX_PAGES + 1;
            }
        }

        // Add page number links
        for (let n = startPage; n <= endPage; n++) {
            const page = PaginationDetailComponent.makePage(n, n, currentPage === n, currentPage === n);
            pages.push(page);
        }

        // Add previous & next links
        const previousPage = PaginationDetailComponent.makePage(currentPage - 1, "&lsaquo;", false, currentPage === 1);
        pages.unshift(previousPage);

        const nextPage = PaginationDetailComponent.makePage(currentPage + 1, "&rsaquo;", false, currentPage === totalPages);
        pages.push(nextPage);

        // Add first & last links
        if (!this.hideBoundaryLinks) {
            const firstPage = PaginationDetailComponent.makePage(1, "&laquo", false, currentPage === 1);
            pages.unshift(firstPage);

            const lastPage = PaginationDetailComponent.makePage(totalPages, "&raquo", false, currentPage === totalPages);
            pages.push(lastPage);
        }

        return pages;
    }

    // Create page object used in template
    private static makePage(n, text, isActive, isDisabled): Page {
        return {
            number: n,
            text: text,
            active: isActive,
            disabled: isDisabled,
        };
    }
}
